import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { Z_INDEX } from '@/constants/layers'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface NotificationInput {
  type: NotificationType
  title: string
  message?: string | undefined
  /** ms. 0 verilirse otomatik kapanmaz. */
  duration?: number | undefined
}

interface NotificationItem extends NotificationInput {
  id: number
}

interface NotificationContextValue {
  notify: (input: NotificationInput) => void
  notifySuccess: (title: string, message?: string) => void
  notifyError: (title: string, message?: string) => void
  notifyWarning: (title: string, message?: string) => void
  notifyInfo: (title: string, message?: string) => void
  /** Eski `useNotification` hook'uyla ayni imza -- mevcut cagri yerleri degismeden calisir. */
  showNotification: (type: NotificationType, title: string, message?: string) => void
  dismiss: (id: number) => void
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined)

/** Ayni anda gorunecek en fazla bildirim; fazlasi en eskiyi duserek yerini alir. */
const MAX_VISIBLE = 4

/** Hatalar daha uzun durur: kullanicinin okuyup aksiyon almasi gerekir. */
const DEFAULT_DURATION: Record<NotificationType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
}

const TYPE_STYLES: Record<NotificationType, { box: string; title: string; body: string; icon: string }> = {
  success: { box: 'border-green-200 bg-green-50', title: 'text-green-900', body: 'text-green-700', icon: 'text-green-600' },
  error: { box: 'border-red-200 bg-red-50', title: 'text-red-900', body: 'text-red-700', icon: 'text-red-600' },
  warning: { box: 'border-yellow-200 bg-yellow-50', title: 'text-yellow-900', body: 'text-yellow-700', icon: 'text-yellow-600' },
  info: { box: 'border-blue-200 bg-blue-50', title: 'text-blue-900', body: 'text-blue-700', icon: 'text-blue-600' },
}

const TypeIcon: React.FC<{ type: NotificationType; className: string }> = ({ type, className }) => {
  if (type === 'success') return <CheckCircle className={className} />
  if (type === 'error') return <XCircle className={className} />
  if (type === 'warning') return <AlertTriangle className={className} />
  return <Info className={className} />
}

/**
 * Uygulama capinda tek bildirim katmani.
 *
 * Neden: onceden 3 ayri sistem vardi -- `useNotification` (tek slotluk lokal state,
 * ardisik iki bildirim birbirini eziyordu) ve `ToastNotification`'in iki kopyasi
 * (biri `type` prop'u olmadigi icin HATALARI YESIL "basarili" kutusunda gosteriyordu).
 *
 * Toast katmani tum modallarin ustundedir; modal disi/arka plan islerinin hatasi
 * modal acikken bile gorunur kalir.
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<NotificationItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const notify = useCallback((input: NotificationInput) => {
    const id = nextId.current++
    const item: NotificationItem = { ...input, id }

    setItems((current) => {
      const next = [...current, item]
      // Kuyruk: eskiyi dusur, yenisi her zaman gorunur.
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next
    })

    const duration = input.duration ?? DEFAULT_DURATION[input.type]
    if (duration > 0) {
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration)
      )
    }
  }, [dismiss])

  const value = useMemo<NotificationContextValue>(() => {
    const withType =
      (type: NotificationType) =>
      (title: string, message?: string) =>
        notify(message === undefined ? { type, title } : { type, title, message })

    return {
      notify,
      dismiss,
      notifySuccess: withType('success'),
      notifyError: withType('error'),
      notifyWarning: withType('warning'),
      notifyInfo: withType('info'),
      showNotification: (type, title, message) =>
        notify(message === undefined ? { type, title } : { type, title, message }),
    }
  }, [notify, dismiss])

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div
        className="fixed top-4 right-4 flex flex-col gap-3 max-w-sm w-full pointer-events-none"
        style={{ zIndex: Z_INDEX.toast }}
        aria-live="polite"
      >
        <AnimatePresence initial={false}>
          {items.map((item) => {
            const styles = TYPE_STYLES[item.type]
            return (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: -24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 24, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={`pointer-events-auto rounded-lg border-2 ${styles.box} p-4 shadow-lg`}
                role={item.type === 'error' ? 'alert' : 'status'}
              >
                <div className="flex items-start gap-3">
                  <TypeIcon type={item.type} className={`w-6 h-6 flex-shrink-0 ${styles.icon}`} />
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium ${styles.title}`}>{item.title}</h3>
                    {item.message && (
                      <p className={`mt-1 text-sm break-words ${styles.body}`}>{item.message}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(item.id)}
                    className={`flex-shrink-0 ${styles.icon} hover:opacity-70 transition-opacity`}
                    aria-label="Kapat"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  )
}

export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications, NotificationProvider icinde kullanilmalidir')
  }
  return context
}
